import {
  LegalLayout, Eyebrow, Title, Updated, H2, H3, P, UL, LI,
  Callout, Contact, BackLink,
} from "../sitechrome";

export default function Terms() {
  return (
    <LegalLayout>
      <Eyebrow>Juridiskt</Eyebrow>
      <Title>Terms of Service</Title>
      <Updated>Last updated: 7 JULY 2026 &nbsp;·&nbsp; Effective date: 7 JULY 2026</Updated>

      <H2 num="1.">Acceptance of these Terms</H2>
      <P>
        By downloading, accessing, or using the App, you confirm that you have read, understood,
        and agree to be bound by these Terms and by our Privacy Policy, which is incorporated by
        reference. If you do not agree, you must not use the Service.
      </P>

      <H2 num="2.">Eligibility</H2>
      <P>
        You must be at least 16 years old to use the Service. If you are under 18, you may only
        use the Service and make purchases with the consent of a parent or legal guardian, who
        accepts these Terms on your behalf. You are responsible for ensuring that your use of the
        Service is lawful in your jurisdiction.
      </P>

      <H2 num="3.">The Service</H2>
      <P>
        The App is a study tool for the Swedish driving-licence theory test (kunskapsprov B). A
        paid subscription provides access to our study materials, which currently include:
      </P>
      <UL>
        <LI>49 reading chapters covering Swedish traffic theory;</LI>
        <LI>all practice quizzes and the full question bank;</LI>
        <LI>mock exams in a format resembling the official test;</LI>
        <LI>progress tracking and a study-readiness indicator; and</LI>
        <LI>content in Swedish, English, Ukrainian, and Russian.</LI>
      </UL>
      <P>
        We may add, modify, or remove features from time to time. The specific content and
        features available may change and are provided as described in the App at the time of use.
      </P>

      <H2 num="4.">Accounts</H2>
      <P>
        To use the Service you sign in using a third-party identity provider (such as Apple or
        Google). You are responsible for maintaining the confidentiality of your account and for
        all activity that occurs under it. You agree to provide accurate information and to notify
        us of any unauthorised use. We may suspend or terminate accounts that violate these Terms.
      </P>

      <H2 num="5.">Subscriptions, pricing and billing</H2>
      <P>
        Access to the full Service requires a paid subscription (the &ldquo;Subscription&rdquo;).
        The price is the amount shown in the App at the time of purchase — for example,
        <strong> 149 SEK</strong> or <strong>USD 14.99</strong> for the applicable subscription
        period. Prices vary by country, currency, and applicable taxes, and may be adjusted from
        time to time. Any price change will apply only to subsequent billing periods and, where
        required by law or platform rules, only after notice to you.
      </P>
      <H3>Billing through app stores</H3>
      <P>
        Subscriptions are purchased and billed through the Apple App Store or Google Play
        (&ldquo;the Store&rdquo;), not directly by us. Payment is charged to your Store account at
        confirmation of purchase.
      </P>
      <H3>Automatic renewal</H3>
      <P>
        Your Subscription <strong>renews automatically</strong> at the then-current price unless
        you cancel it at least 24 hours before the end of the current period. Your Store account
        is charged for renewal within the 24-hour period before the period ends.
      </P>
      <H3>Managing and cancelling</H3>
      <P>
        You can manage or cancel your Subscription at any time in your Store account settings.
        Cancellation takes effect at the end of the current paid period; you retain access until
        then. Deleting the App does not cancel a Subscription.
      </P>

      <H2 num="6.">Right of withdrawal and refunds</H2>
      <P>
        As a consumer in the EU/EEA you normally have a 14-day right of withdrawal for distance
        contracts. However, for digital content and services supplied immediately, you expressly
        request immediate access upon purchase and acknowledge that you thereby{" "}
        <strong>lose your right of withdrawal</strong> once performance has begun, to the extent
        permitted by applicable law.
      </P>
      <P>
        Because billing is handled by the Store, <strong>refunds are subject to the policies of
        Apple or Google</strong> and are requested directly through them. This section does not
        limit any mandatory statutory consumer rights you may have.
      </P>

      <H2 num="7.">Educational purpose — no guarantee of passing</H2>
      <P>This is the most important part of these Terms, so please read it carefully.</P>
      <Callout>
        <P>
          The Service is provided for <strong>study and informational purposes only</strong>. The
          readiness indicator is an <strong>estimate based solely on your activity within the
          App</strong>. It is <strong>not a prediction, assessment, or guarantee</strong> that you
          will pass the official theory test, and it must not be relied on as such.
        </P>
        <P>
          <strong>The decision to book, pay for, or take the official test is entirely your own.</strong>{" "}
          We are not responsible for test fees, failed attempts, missed appointments, or any other
          cost or consequence arising from your decision to take the test, regardless of what the
          App indicated about your readiness.
        </P>
      </Callout>
      <P>
        We are an independent study provider and are <strong>not affiliated with, endorsed by, or
        connected to Trafikverket, Transportstyrelsen, or any government authority</strong>. The
        official theory test, its questions, rules, and results are determined solely by those
        authorities. While we work to keep our content accurate and up to date, driving rules
        change and errors may occur; the official sources always take precedence, and you should
        consult them.
      </P>

      <H2 num="8.">Licence and intellectual property</H2>
      <P>
        We grant you a limited, personal, non-exclusive, non-transferable, revocable licence to
        use the App for your own private study. All content — including chapters, questions,
        explanations, images, design, and software — is owned by us or our licensors and is
        protected by intellectual-property laws. You may not copy, reproduce, distribute, sell,
        publish, scrape, or create derivative works from any part of the Service without our prior
        written permission.
      </P>

      <H2 num="9.">Acceptable use</H2>
      <P>You agree not to:</P>
      <UL>
        <LI>share, resell, or provide access to your account or the content to others;</LI>
        <LI>reverse engineer, decompile, or attempt to extract the source code or question bank;</LI>
        <LI>use bots, scrapers, or automated means to access or copy the Service;</LI>
        <LI>interfere with or disrupt the Service or its security; or</LI>
        <LI>use the Service for any unlawful purpose.</LI>
      </UL>

      <H2 num="10.">Third-party services</H2>
      <P>
        The Service relies on third parties — including Apple, Google, and our subscription
        provider (RevenueCat) — for sign-in, billing, and delivery. Your use of those services is
        governed by their own terms and privacy policies, and we are not responsible for their
        acts or omissions.
      </P>

      <H2 num="11.">Disclaimer of warranties</H2>
      <P>
        To the fullest extent permitted by law, the Service is provided <strong>&ldquo;as is&rdquo;
        and &ldquo;as available&rdquo;</strong> without warranties of any kind, whether express or
        implied, including as to accuracy, fitness for a particular purpose, or uninterrupted
        availability. Nothing in this section removes or limits any warranty or right that cannot
        be excluded under mandatory Swedish or EU consumer law.
      </P>

      <H2 num="12.">Limitation of liability</H2>
      <P>
        To the fullest extent permitted by law, we are not liable for any indirect, incidental, or
        consequential damages, or for loss of profits, data, or opportunity, arising from your use
        of — or inability to use — the Service. This expressly includes any outcome of the official
        theory test.
      </P>
      <P>
        Where we are found liable, our total aggregate liability to you is limited to the amount
        you paid for the Subscription in the twelve (12) months preceding the event giving rise to
        the claim.
      </P>
      <P>
        <strong>Nothing in these Terms excludes or limits our liability where it would be unlawful
        to do so</strong> — including liability for death or personal injury caused by our
        negligence, for fraud, or for any mandatory rights you have as a consumer.
      </P>

      <H2 num="13.">Changes to the Service and these Terms</H2>
      <P>
        We may update the Service or these Terms from time to time. If we make material changes,
        we will provide reasonable notice (for example, in the App or by updating the
        &ldquo;Last updated&rdquo; date). Your continued use after changes take effect constitutes
        acceptance. If you do not agree to the updated Terms, you should stop using the Service and
        may cancel your Subscription.
      </P>

      <H2 num="14.">Termination</H2>
      <P>
        You may stop using the Service at any time. We may suspend or terminate your access if you
        breach these Terms or use the Service unlawfully. On termination, the licence in Section 8
        ends; sections that by their nature should survive (including intellectual property,
        disclaimers, and limitation of liability) continue to apply.
      </P>

      <H2 num="15.">Governing law and disputes</H2>
      <P>
        These Terms are governed by Swedish law, without prejudice to the mandatory consumer
        protection rules of your country of residence. If you are a consumer, you may bring a
        complaint to the Swedish National Board for Consumer Disputes (Allmänna
        reklamationsnämnden, ARN,{" "}
        <a href="https://www.arn.se" target="_blank" rel="noopener noreferrer">arn.se</a>) or use
        the EU Online Dispute Resolution platform (
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </a>
        ). Disputes not otherwise resolved may be brought before the competent Swedish courts.
      </P>

      <H2 num="16.">Privacy</H2>
      <P>
        Our handling of personal data is described in our{" "}
        <a href="/privacy">Privacy Policy</a>, which forms part of these Terms.
      </P>

      <Contact
        title="Contact"
        lines={[
          "teorikorkort.se",
          <>Email: <a href="mailto:support@teorikorkort.se">support@teorikorkort.se</a></>,
        ]}
      />

      <BackLink href="/sv/">← Till startsidan</BackLink>
    </LegalLayout>
  );
}